import type {
	CookieConsentCategoryConfig,
	CookieConsentCategoryKey,
	CookieConsentCategorySelection,
	CookieConsentConfig,
	CookieConsentDecisionStatus,
	CookieConsentStoredState,
	CookieConsentVendorKey,
} from "../../types/src/seo";

export const COOKIE_CONSENT_CATEGORIES = [
	"essential",
	"preferences",
	"analytics",
	"marketing",
] as const satisfies readonly CookieConsentCategoryKey[];

export const OPTIONAL_COOKIE_CONSENT_CATEGORIES = [
	"preferences",
	"analytics",
	"marketing",
] as const satisfies readonly Exclude<CookieConsentCategoryKey, "essential">[];

export const COOKIE_CONSENT_VENDORS = [
	"gtm",
	"ga4",
	"microsoft_clarity",
] as const satisfies readonly CookieConsentVendorKey[];

const DEFAULT_COOKIE_CONSENT_CONFIG: CookieConsentConfig = {
	accept_all_label: "Accept all",
	banner_description:
		"We use cookies to improve site performance, remember preferences, and measure marketing effectiveness. You can accept all cookies, reject non-essential ones, or choose categories.",
	banner_position: "bottom-right",
	banner_title: "Your privacy choices",
	categories: {
		analytics: {
			default_enabled: false,
			description:
				"Analytics cookies help us understand traffic and content performance.",
			editable: true,
			enabled: true,
			label: "Analytics",
			required: false,
		},
		essential: {
			default_enabled: true,
			description:
				"Essential cookies keep the site secure and make core functionality work.",
			editable: false,
			enabled: true,
			label: "Essential",
			required: true,
		},
		marketing: {
			default_enabled: false,
			description:
				"Marketing cookies help us measure campaigns and tailor promotions.",
			editable: true,
			enabled: true,
			label: "Marketing",
			required: false,
		},
		preferences: {
			default_enabled: false,
			description:
				"Preference cookies remember choices such as language and display settings.",
			editable: true,
			enabled: true,
			label: "Preferences",
			required: false,
		},
	},
	consent_version: 1,
	cookie_max_age_days: 180,
	cookie_name: "tenant_cookie_consent",
	cookie_policy_url: null,
	customize_label: "Customize",
	enabled: false,
	manage_preferences_label: "Cookie settings",
	preferences_description:
		"Choose which categories you allow. Essential cookies stay on because the site needs them to function.",
	preferences_title: "Manage cookie preferences",
	privacy_policy_url: null,
	reject_all_label: "Reject non-essential",
	save_preferences_label: "Save preferences",
	vendors: {
		ga4: {
			category: "analytics",
			description:
				"Google Analytics 4 measures visits, traffic sources, and content engagement.",
			enabled: true,
			label: "Google Analytics 4",
		},
		gtm: {
			category: "marketing",
			description:
				"Google Tag Manager loads approved marketing and measurement tags.",
			enabled: true,
			label: "Google Tag Manager",
		},
		microsoft_clarity: {
			category: "analytics",
			description:
				"Microsoft Clarity helps understand on-site behavior through session analytics.",
			enabled: true,
			label: "Microsoft Clarity",
		},
	},
};

type UnknownRecord = Record<string, unknown>;

function asObject(value: unknown): UnknownRecord {
	return typeof value === "object" && value !== null && !Array.isArray(value)
		? (value as UnknownRecord)
		: {};
}

function asBoolean(value: unknown, fallback: boolean): boolean {
	return typeof value === "boolean" ? value : fallback;
}

function asNullableString(
	value: unknown,
	fallback: string | null
): string | null {
	if (typeof value !== "string") {
		return fallback;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : null;
}

function asString(value: unknown, fallback: string): string {
	if (typeof value !== "string") {
		return fallback;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : fallback;
}

function asPositiveInt(value: unknown, fallback: number): number {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return fallback;
	}
	const next = Math.trunc(value);
	return next > 0 ? next : fallback;
}

function normalizeCategory(
	raw: unknown,
	fallback: CookieConsentCategoryConfig,
	force: Partial<CookieConsentCategoryConfig> = {}
): CookieConsentCategoryConfig {
	const object = asObject(raw);
	return {
		default_enabled:
			force.default_enabled ??
			asBoolean(
				object.default_enabled ?? object.defaultEnabled,
				fallback.default_enabled
			),
		description: asString(object.description, fallback.description),
		editable: force.editable ?? asBoolean(object.editable, fallback.editable),
		enabled: force.enabled ?? asBoolean(object.enabled, fallback.enabled),
		label: asString(object.label, fallback.label),
		required: force.required ?? asBoolean(object.required, fallback.required),
	};
}

function normalizeSelection(
	config: CookieConsentConfig,
	value: unknown
): CookieConsentCategorySelection {
	const object = asObject(value);
	const base = createRejectedCookieConsentSelection(config);
	const next: CookieConsentCategorySelection = { ...base };
	for (const category of OPTIONAL_COOKIE_CONSENT_CATEGORIES) {
		if (!config.categories[category].enabled) {
			next[category] = false;
			continue;
		}
		next[category] = asBoolean(object[category], base[category]);
	}
	next.essential = true;
	return next;
}

function resolveBannerPosition(
	raw: unknown,
	fallback: CookieConsentConfig["banner_position"]
): CookieConsentConfig["banner_position"] {
	if (
		raw === "bottom-left" ||
		raw === "bottom-center" ||
		raw === "bottom-right"
	) {
		return raw;
	}
	return fallback;
}

function resolveVendorCategory(
	raw: unknown,
	fallback: "analytics" | "marketing"
): "analytics" | "marketing" {
	return raw === "analytics" || raw === "marketing" ? raw : fallback;
}

function normalizeVendor(
	raw: unknown,
	fallback: CookieConsentConfig["vendors"][keyof CookieConsentConfig["vendors"]],
	categoryFallback: "analytics" | "marketing"
) {
	const object = asObject(raw);
	return {
		category: resolveVendorCategory(object.category, categoryFallback),
		description: asString(object.description, fallback.description),
		enabled: asBoolean(object.enabled, fallback.enabled),
		label: asString(object.label, fallback.label),
	};
}

export function defaultCookieConsentConfig(): CookieConsentConfig {
	return structuredClone(DEFAULT_COOKIE_CONSENT_CONFIG);
}

export function normalizeCookieConsentConfig(
	raw: unknown
): CookieConsentConfig {
	const object = asObject(raw);
	const fallback = defaultCookieConsentConfig();
	const categories = asObject(object.categories);
	const vendors = asObject(object.vendors);
	const bannerPosition = resolveBannerPosition(
		object.banner_position ?? object.bannerPosition,
		fallback.banner_position
	);

	return {
		accept_all_label: asString(
			object.accept_all_label ?? object.acceptAllLabel,
			fallback.accept_all_label
		),
		banner_description: asString(
			object.banner_description ?? object.bannerDescription,
			fallback.banner_description
		),
		banner_position: bannerPosition,
		banner_title: asString(
			object.banner_title ?? object.bannerTitle,
			fallback.banner_title
		),
		categories: {
			analytics: normalizeCategory(
				categories.analytics,
				fallback.categories.analytics
			),
			essential: normalizeCategory(
				categories.essential,
				fallback.categories.essential,
				{
					default_enabled: true,
					editable: false,
					enabled: true,
					required: true,
				}
			),
			marketing: normalizeCategory(
				categories.marketing,
				fallback.categories.marketing
			),
			preferences: normalizeCategory(
				categories.preferences,
				fallback.categories.preferences
			),
		},
		consent_version: asPositiveInt(
			object.consent_version ?? object.consentVersion,
			fallback.consent_version
		),
		cookie_max_age_days: asPositiveInt(
			object.cookie_max_age_days ?? object.cookieMaxAgeDays,
			fallback.cookie_max_age_days
		),
		cookie_name: asString(
			object.cookie_name ?? object.cookieName,
			fallback.cookie_name
		),
		cookie_policy_url: asNullableString(
			object.cookie_policy_url ?? object.cookiePolicyUrl,
			fallback.cookie_policy_url
		),
		customize_label: asString(
			object.customize_label ?? object.customizeLabel,
			fallback.customize_label
		),
		enabled: asBoolean(object.enabled, fallback.enabled),
		manage_preferences_label: asString(
			object.manage_preferences_label ?? object.managePreferencesLabel,
			fallback.manage_preferences_label
		),
		preferences_description: asString(
			object.preferences_description ?? object.preferencesDescription,
			fallback.preferences_description
		),
		preferences_title: asString(
			object.preferences_title ?? object.preferencesTitle,
			fallback.preferences_title
		),
		privacy_policy_url: asNullableString(
			object.privacy_policy_url ?? object.privacyPolicyUrl,
			fallback.privacy_policy_url
		),
		reject_all_label: asString(
			object.reject_all_label ?? object.rejectAllLabel,
			fallback.reject_all_label
		),
		save_preferences_label: asString(
			object.save_preferences_label ?? object.savePreferencesLabel,
			fallback.save_preferences_label
		),
		vendors: {
			ga4: normalizeVendor(vendors.ga4, fallback.vendors.ga4, "analytics"),
			gtm: normalizeVendor(vendors.gtm, fallback.vendors.gtm, "marketing"),
			microsoft_clarity: normalizeVendor(
				vendors.microsoft_clarity,
				fallback.vendors.microsoft_clarity,
				"analytics"
			),
		},
	};
}

export function createAcceptedCookieConsentSelection(
	config: CookieConsentConfig
): CookieConsentCategorySelection {
	return {
		analytics: config.categories.analytics.enabled,
		essential: true,
		marketing: config.categories.marketing.enabled,
		preferences: config.categories.preferences.enabled,
	};
}

export function createRejectedCookieConsentSelection(
	_config: CookieConsentConfig
): CookieConsentCategorySelection {
	return {
		analytics: false,
		essential: true,
		marketing: false,
		preferences: false,
	};
}

export function createStoredCookieConsentState(
	config: CookieConsentConfig,
	status: CookieConsentDecisionStatus,
	selection: CookieConsentCategorySelection
): CookieConsentStoredState {
	return {
		categories: normalizeSelection(config, selection),
		consent_version: config.consent_version,
		decided_at: new Date().toISOString(),
		status,
	};
}

export function parseCookieConsentValue(
	value: string | null | undefined
): CookieConsentStoredState | null {
	if (!value) {
		return null;
	}
	try {
		const decoded = decodeURIComponent(value);
		const parsed = JSON.parse(decoded) as unknown;
		const object = asObject(parsed);
		const status = object.status;
		if (
			status !== "accepted_all" &&
			status !== "customized" &&
			status !== "rejected_non_essential"
		) {
			return null;
		}
		const decidedAt = asString(object.decided_at ?? object.decidedAt, "");
		const consentVersion = asPositiveInt(
			object.consent_version ?? object.consentVersion,
			0
		);
		if (!decidedAt || consentVersion <= 0) {
			return null;
		}
		return {
			categories: {
				analytics: asBoolean(asObject(object.categories).analytics, false),
				essential: true,
				marketing: asBoolean(asObject(object.categories).marketing, false),
				preferences: asBoolean(asObject(object.categories).preferences, false),
			},
			consent_version: consentVersion,
			decided_at: decidedAt,
			status,
		};
	} catch {
		return null;
	}
}

export function serializeCookieConsentValue(
	state: CookieConsentStoredState
): string {
	return encodeURIComponent(JSON.stringify(state));
}

export function buildCookieConsentDocumentValue(
	config: CookieConsentConfig,
	state: CookieConsentStoredState,
	options?: { secure?: boolean }
): string {
	const parts = [
		`${encodeURIComponent(config.cookie_name)}=${serializeCookieConsentValue(state)}`,
		`Max-Age=${config.cookie_max_age_days * 24 * 60 * 60}`,
		"Path=/",
		"SameSite=Lax",
	];
	if (options?.secure) {
		parts.push("Secure");
	}
	return parts.join("; ");
}

export function resolveCookieConsentState(
	config: CookieConsentConfig,
	cookieValue: string | null | undefined
): {
	categories: CookieConsentCategorySelection;
	hasStoredConsent: boolean;
	shouldPrompt: boolean;
	stored: CookieConsentStoredState | null;
} {
	const stored = parseCookieConsentValue(cookieValue);
	if (!config.enabled) {
		return {
			categories: createAcceptedCookieConsentSelection(config),
			hasStoredConsent: stored !== null,
			shouldPrompt: false,
			stored,
		};
	}
	if (!stored || stored.consent_version !== config.consent_version) {
		return {
			categories: createRejectedCookieConsentSelection(config),
			hasStoredConsent: false,
			shouldPrompt: true,
			stored: null,
		};
	}
	return {
		categories: normalizeSelection(config, stored.categories),
		hasStoredConsent: true,
		shouldPrompt: false,
		stored: {
			...stored,
			categories: normalizeSelection(config, stored.categories),
		},
	};
}

export function isConsentCategoryAllowed(
	config: CookieConsentConfig,
	selection: CookieConsentCategorySelection,
	category: CookieConsentCategoryKey
): boolean {
	if (!config.enabled || category === "essential") {
		return true;
	}
	if (!config.categories[category].enabled) {
		return false;
	}
	return selection[category];
}

export function isConsentVendorAllowed(
	config: CookieConsentConfig,
	selection: CookieConsentCategorySelection,
	vendor: CookieConsentVendorKey
): boolean {
	if (!config.enabled) {
		return true;
	}
	if (!config.vendors[vendor].enabled) {
		return false;
	}
	return isConsentCategoryAllowed(
		config,
		selection,
		config.vendors[vendor].category
	);
}
